import { PRIVACY_URL } from './dgpt'

export const HERO_POINTS = [
  'Your investment criteria',
  'Structured assessment frameworks',
  'Findings for analyst review',
] as const

export const HOW_IT_WORKS = [
  {
    label: 'Input',
    title: 'Add company information',
    body: 'Start with a pitch deck and add relevant company documents.',
    chips: ['Pitch deck', 'Company documents', 'Data room files'],
  },
  {
    label: 'Assessment',
    title: 'Apply your investment criteria',
    body: 'Assess the opportunity using the criteria and frameworks relevant to your fund.',
    chips: ['Thesis criteria', 'Fit by criterion', 'Risk review'],
  },
  {
    label: 'Output',
    title: 'Review the findings',
    body: 'Review the assessment, investigate gaps, and prepare your investment memo.',
    chips: ['Strengths & risks', 'Open questions', 'Memo draft'],
  },
] as const

export const WHY_POINTS = [
  {
    title: 'Fund-specific criteria',
    body: 'Set your thesis once — sector, stage, geography, check size and the questions your team actually asks — and assess every company against it.',
  },
  {
    title: 'Structured frameworks',
    body: 'Each company gets the same report structure — tearsheet, market, business risk, metrics, comparables and gap analysis — so analysis is comparable across deals.',
  },
  {
    title: 'Evidence and information gaps',
    body: 'Findings point back to the deck slide or document behind them, and missing information is listed separately from what is supported.',
  },
  {
    title: 'Connected work',
    body: 'Company information stays with the deal in your pipeline, so screening, research and memo preparation build on each other instead of starting over.',
  },
] as const

export const POSITIONING = [
  {
    question: 'How is this different from asking a general AI assistant?',
    answer:
      'A general assistant starts from a blank prompt each time. DiligenceGPT™ keeps your fund’s thesis criteria as a saved configuration, runs every company through the same set of reports, and keeps each company’s documents and findings together in your deal pipeline. Your team gets consistent outputs it can compare across deals, with sources it can check.',
  },
  {
    question: 'Does this replace our CRM or sourcing database?',
    answer:
      'No. DiligenceGPT™ focuses on assessment and memo preparation. It includes a deal pipeline for tracking diligence, and deals can come in through a branded founder portal, email forwarding, or Google Drive and DocSend links. We don’t list CRM integrations — if your team relies on one, ask us how it would fit alongside your setup.',
  },
] as const

export const HOME_FAQ: { question: string; answer: string; link?: { label: string; href: string } }[] = [
  {
    question: 'What does DiligenceGPT™ do?',
    answer:
      'It helps investment teams screen startups, review company documents and prepare investment assessments and memos using the fund’s own criteria. Outputs are drafts for analyst review.',
  },
  {
    question: 'Who is it designed for?',
    answer:
      'Analysts, associates and partners at venture funds, corporate venture teams, family offices, accelerators and other organizations that assess startups.',
  },
  {
    question: 'What documents do I need to get started?',
    answer:
      'A pitch deck is enough to start. Adding a financial model and data room documents gives the assessment more to work with and reduces open questions.',
  },
  {
    question: 'Can I use my fund’s investment criteria?',
    answer:
      'Yes. You set your thesis — sectors, stage, geography, check size and other requirements — and companies are assessed against those criteria rather than a generic framework.',
  },
  {
    question: 'Can I see a sample investment memo?',
    answer:
      'Yes. The sample memo on this site uses a fictional company to show the structure, sources and open questions you’d receive. No email address is required.',
    link: { label: 'Read the sample memo', href: '/sample-memo' },
  },
  {
    question: 'How should analysts review AI-generated findings?',
    answer:
      'Treat them as a first draft. Check each finding against its source, confirm material figures with the company, follow up on open questions and apply your team’s judgment before anything goes to IC.',
  },
  {
    question: 'How does DiligenceGPT™ fit alongside existing tools?',
    answer:
      'It handles assessment and memo preparation. Your CRM and sourcing tools stay where they are; DiligenceGPT™ is where companies get screened, researched and written up.',
  },
  {
    question: 'How is company information handled?',
    answer:
      'As currently published on dgpt.io: customer deal data is not used to train our models or third-party models, workspaces are tenant-isolated, and a SOC 2 audit is in progress (not yet certified). See the Privacy Policy for full details.',
    link: { label: 'Privacy Policy', href: PRIVACY_URL },
  },
]
